import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { MultiSelect } from '@hotjar/hotsauce';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';

import { ALL_HIGHLIGHTS_ID } from '@hotjar/webapp/domains/collections/model';

import { useCollectionsList } from './use-collections-list';
import { ExistingCollectionError, useCollectionCreate } from './use-collection-create';

type CollectionId = string;

function mapCollectionsToOptions(
  collections: Array<{ id: CollectionId; name: string }> | undefined,
): Array<{ label: string; value: string }> {
  if (!collections) {
    return [];
  }

  return collections
    .filter(({ id }) => id !== ALL_HIGHLIGHTS_ID)
    .map(({ id, name }) => ({ label: name, value: id }));
}

function mapSelectedCollectionsId(
  collections: Array<{ id: CollectionId; name: string }> | undefined,
  selectedCollections: Set<CollectionId>,
): Array<{ label: string; value: string }> {
  if (!collections || !selectedCollections) {
    return [];
  }

  return collections
    .filter(({ id }) => selectedCollections.has(id))
    .map(({ id, name }) => ({ label: name, value: id }));
}

interface CollectionsSelectorProps {
  className?: string;
  onAdd?: (addedCollectionId: CollectionId) => void;
  onEventSend: () => void;
  onRemove?: (removedCollectionId: CollectionId) => void;
  onSelectedIdsChange: (selectedCollectionIds: Set<CollectionId>) => void;
  selectedCollectionIds: Set<CollectionId>;
  siteId: string;
}

export function CollectionsSelector({
  className,
  onAdd,
  onEventSend,
  onRemove,
  onSelectedIdsChange,
  selectedCollectionIds,
  siteId,
}: CollectionsSelectorProps): JSX.Element {
  const { t } = useTranslation();

  const { mutateAsync: create } = useCollectionCreate(siteId);
  const { success, error } = useNotify();

  const { data: availableCollections } = useCollectionsList(siteId);
  const availableOptions = useMemo(
    () => [...mapCollectionsToOptions(availableCollections)],
    [availableCollections],
  );
  const selectedOptions = useMemo(
    () => mapSelectedCollectionsId(availableCollections, selectedCollectionIds),
    [availableCollections, selectedCollectionIds],
  );

  const handleChange = useCallback(
    (newSelectedCollections: Array<{ label: CollectionId; value: string }>) => {
      const newSelectedCollectionIds = newSelectedCollections.map(({ value }) => value);
      const newSelectedCollectionIdsSet = new Set(newSelectedCollectionIds);

      if (onAdd) {
        newSelectedCollectionIds.filter((id) => !selectedCollectionIds.has(id)).forEach(onAdd);
      }

      if (onRemove) {
        Array.from(selectedCollectionIds)
          .filter((id) => !newSelectedCollectionIdsSet.has(id))
          .forEach(onRemove);
      }

      if (selectedCollectionIds.size < newSelectedCollectionIds.length) {
        onEventSend();
      }

      onSelectedIdsChange(newSelectedCollectionIdsSet);
    },
    [onAdd, onEventSend, onSelectedIdsChange, onRemove, selectedCollectionIds],
  );
  const handleNewCollectionCreated = useCallback(
    (newCollectionId: string) => {
      const newSelectedCollectionIdsSet = new Set(selectedCollectionIds);
      newSelectedCollectionIdsSet.add(newCollectionId);
      onSelectedIdsChange(newSelectedCollectionIdsSet);
      onAdd?.(newCollectionId);
      onEventSend();
    },
    [onAdd, onEventSend, onSelectedIdsChange, selectedCollectionIds],
  );

  const handleCreateCollection = useCallback(
    async (name: string) => {
      try {
        const newCollectionId = await create({ name });
        success(t('collections:create.success'));
        handleNewCollectionCreated(newCollectionId);
      } catch (err) {
        if (err instanceof ExistingCollectionError) {
          return;
        }

        error(t('collections:create.error.unknown'));
      }
    },
    [error, handleNewCollectionCreated, create, success, t],
  );

  return (
    <MultiSelect
      size="s"
      className={className}
      availableOptions={availableOptions}
      selectedOptions={selectedOptions}
      onChange={handleChange}
      placeholder={t('collections:selector.placeholder')}
      hasAddOption
      onAddOption={handleCreateCollection}
    />
  );
}
